import { RequestBuilder, fetchAndRetryIfServerBusy } from '../utils/fetch';
import { SESSION_HEADER_EVENT_ID } from '../constants/Request';
import { ServiceError } from '../utils/errors';

/**
 * Universal Webcast Client
 */
export default class UniversalWebcastClient {
  constructor(baseURL, eventId, environment, authorizationToken) {
    this.webcastBaseUrl = baseURL + 'universal-webcast/v1/webcasts';
    this.environment = environment;
    this.requestBuilder = new RequestBuilder({ url: this.webcastBaseUrl })
      .auth(authorizationToken)
      .withCookies()
      .header(SESSION_HEADER_EVENT_ID, eventId);
  }

  /**
   * Method to get any Webcast(event level or session level) from universal webcast service
   * @param webcastId - Can be a session webcast ID or event webcast ID
   * @param authToken
   * @returns {Promise<*>}
   */
  async getWebcast(webcastId) {
    const request = this.requestBuilder
      .url(`${this.webcastBaseUrl}/${webcastId}`)
      .query('environment', this.environment)
      .get()
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      throw await ServiceError.create('Error retrieving webcast data', response, request);
    }
    return response.json();
  }

  /**
   * Method to get Session Webcast details from universal webcast service
   * @param sessionId - ID of the session webcast
   * @param authToken
   * @returns {Promise<*>}
   */
  async getSessionWebcast(sessionId) {
    const request = this.requestBuilder
      .query('environment', this.environment)
      .query('filter', `session.id eq '${sessionId}'`)
      .get()
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      throw await ServiceError.create('Error retrieving session webcasts data', response, request);
    }
    return response.json();
  }

  /**
   * Method to get list of all webcasts(event and session) associated to an event from universal webcast service
   * @param eventId - event ID of the event whose webcasts needs to be fetched.
   * @param authToken
   * @returns {Promise<*>}
   */
  async getWebcastsList(eventId) {
    const request = this.requestBuilder
      .query('environment', this.environment)
      .query('filter', `event.id eq '${eventId}'`)
      .get()
      .build();
    const response = await fetchAndRetryIfServerBusy(request);
    if (!response.ok) {
      throw await ServiceError.create('Error retrieving webcasts data', response, request);
    }
    return response.json();
  }
}
